import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { ASSIGNMENT_HISTORY_URL } from "../../constants/api";
import { formatDateTime } from "../../utils/datetime";
import { DecisionTrail, MethodPill } from "./shared";

/**
 * "Why does this rep own this lead?" plus every owner change, newest first.
 * Reads customer/assignment/history/<lead_id>/. Owner changes made before the
 * assignment engine existed show as "Before tracking", with no reason.
 */
export default function OwnerHistory({ leadId }) {
    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [showTrail, setShowTrail] = useState(false);
    const [openReq, setOpenReq] = useState(null);

    useEffect(() => {
        if (!leadId) return;
        setData(null); setError('');
        fetch(`${ASSIGNMENT_HISTORY_URL}${leadId}/`).then(res => {
            if (res?.status === 'success') setData(res);
            else setError(res?.message || 'Could not load the owner history');
        });
    }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (error) return <p className="text-xs text-gray-400 p-3">{error}</p>;
    if (!data) return <p className="text-xs text-gray-400 p-3">Loading…</p>;

    const why = data.why;
    const unresolved = (data.requests || []).filter(r => r.status === 'unassigned' || r.status === 'failed');

    return (
        <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-white">
                    <HelpCircle className="h-3.5 w-3.5 text-primary" />
                    Why {data.lead.owner ? `${data.lead.owner.name} owns this lead` : 'this lead has no owner'}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                    {why?.summary || (data.lead.owner
                        ? 'No reason was recorded for this owner.'
                        : unresolved[0]?.reason || 'Nobody has been assigned yet.')}
                </p>
                {why?.decision && (
                    <button type="button" onClick={() => setShowTrail(s => !s)}
                        className="mt-2 text-[11px] text-primary hover:underline inline-flex items-center gap-0.5">
                        {showTrail ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        Show every step
                    </button>
                )}
                {showTrail && why?.decision && <div className="mt-2"><DecisionTrail decision={why.decision} /></div>}
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                        <tr>
                            {['When', 'From', 'To', 'How', 'By', 'Reason'].map(h =>
                                <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {data.history.map(h => (
                            <tr key={h.id}>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">{formatDateTime(h.when)}</td>
                                <td className="px-3 py-2 text-gray-500">{h.from?.name || 'Unassigned'}</td>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{h.to?.name || 'Unassigned'}</td>
                                <td className="px-3 py-2"><MethodPill method={h.method} />
                                    {h.rule && <span className="ml-1 text-[10px] text-gray-400">{h.rule.name} v{h.rule.version}</span>}</td>
                                <td className="px-3 py-2 text-gray-500">{h.by?.name}</td>
                                <td className="px-3 py-2 text-gray-500 max-w-xs">{h.reason || '—'}</td>
                            </tr>
                        ))}
                        {!data.history.length && (
                            <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No owner changes yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {(data.requests || []).length > 0 && (
                <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Automatic assignment runs</p>
                    <div className="space-y-1">
                        {data.requests.map(r => (
                            <div key={r.id} className="rounded border border-gray-100 dark:border-gray-700">
                                <button type="button" onClick={() => setOpenReq(openReq === r.id ? null : r.id)}
                                    className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[11px]">
                                    <span className="text-gray-600 dark:text-gray-300">
                                        {formatDateTime(r.created_date)} · {r.trigger_label} ·{' '}
                                        <b className={r.status === 'assigned' ? 'text-emerald-600' : r.status === 'skipped' ? 'text-gray-500' : 'text-amber-600'}>{r.status}</b>
                                        {r.result && <> → {r.result.name}</>}
                                    </span>
                                    {openReq === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                </button>
                                {openReq === r.id && (
                                    <div className="px-2.5 pb-2">
                                        {r.decision ? <DecisionTrail decision={r.decision} />
                                            : <p className="text-[11px] text-gray-500">{r.reason}</p>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
